import React from "react";
import axios from "axios";
import { MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MESSENGER_URL } from "@/data/staycation";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FAQs() {
  const [faqs, setFaqs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let active = true;
    axios.get(`${API}/faqs`)
      .then((r) => active && setFaqs(r.data))
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  return (
    <div data-testid="faqs-page">
      <Seo
        title="FAQs — Booking, check-in, payment & more"
        description="Common questions about booking a Xanders Staycation: how to book via Messenger, check-in/out times, downpayment, guest limits, amenities, MOA proximity, and airport transfers."
        path="/faqs"
      />
      <section className="mx-auto max-w-3xl px-5 lg:px-8 pt-14 pb-12 text-center">
        <p className="xs-eyebrow">Frequently Asked Questions</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-2">Everything you need to know.</h1>
        <p className="mt-4 text-neutral-600">Can't find your answer? Just send us a message — we usually reply within minutes.</p>
      </section>

      <section className="mx-auto max-w-3xl px-5 lg:px-8 pb-16">
        {loading && <div className="text-center text-neutral-500" data-testid="faqs-loading">Loading…</div>}
        {!loading && (
          <Accordion type="single" collapsible className="space-y-3" data-testid="faqs-accordion">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-neutral-200 rounded-2xl bg-white px-5 data-[state=open]:border-[var(--xs-primary)]"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="font-display text-base sm:text-lg font-semibold py-5 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-neutral-600 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </section>

      <section className="mx-auto max-w-3xl px-5 lg:px-8 pb-24">
        <div className="rounded-3xl bg-[var(--xs-bg-alt)] border border-neutral-200 p-8 text-center">
          <h3 className="font-display text-2xl font-bold">Still have questions?</h3>
          <p className="mt-2 text-sm text-neutral-600">Chat with Kuya Alex directly on Messenger for instant answers.</p>
          <a
            href={MESSENGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faqs-messenger-cta"
            className="xs-btn-primary mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
          >
            <MessageCircle className="h-4 w-4" /> Message us
          </a>
        </div>
      </section>
    </div>
  );
}
